import { useEffect, useMemo, useState } from "react";
import { getSpecialInstructions } from "../API/getSpecialInstructions";
import { getAccountCustomerCode } from "../helpers";

export type TInstructionTarget = "shipper" | "consignee";

interface IUseAccountSpecialInstructionsParams {
  shipperAccount?: string | number | null;
  consigneeAccount?: string | number | null;
  enabled?: boolean;
}

export const useAccountSpecialInstructions = ({
  shipperAccount,
  consigneeAccount,
  enabled = true,
}: IUseAccountSpecialInstructionsParams) => {
  const [shipperSpecialInstructions, setShipperSpecialInstructions] =
    useState<string>("");
  const [consigneeSpecialInstructions, setConsigneeSpecialInstructions] =
    useState<string>("");
  const [openInstructionsFor, setOpenInstructionsFor] =
    useState<TInstructionTarget | null>(null);

  const normalizedShipperAccount = useMemo(
    () => getAccountCustomerCode(shipperAccount),
    [shipperAccount],
  );

  const normalizedConsigneeAccount = useMemo(
    () => getAccountCustomerCode(consigneeAccount),
    [consigneeAccount],
  );

  useEffect(() => {
    if (!enabled) {
      setShipperSpecialInstructions("");
      setConsigneeSpecialInstructions("");
      setOpenInstructionsFor(null);
      return;
    }

    if (!normalizedShipperAccount && !normalizedConsigneeAccount) {
      setShipperSpecialInstructions("");
      setConsigneeSpecialInstructions("");
      setOpenInstructionsFor(null);
      return;
    }

    let active = true;

    const fetchInstructionsByCustomerCode = async (customerCode: string) => {
      try {
        const response = await getSpecialInstructions(customerCode);
        return response?.specialInstructions?.trim() ?? "";
      } catch (error) {
        console.error(
          `Failed to fetch special instructions for account ${customerCode}`,
          error,
        );
        return "";
      }
    };

    const loadSpecialInstructions = async () => {
      if (
        normalizedShipperAccount &&
        normalizedConsigneeAccount &&
        normalizedShipperAccount === normalizedConsigneeAccount
      ) {
        const sharedInstructions =
          await fetchInstructionsByCustomerCode(normalizedShipperAccount);

        if (!active) {
          return;
        }

        setShipperSpecialInstructions(sharedInstructions);
        setConsigneeSpecialInstructions(sharedInstructions);
        return;
      }

      const [shipperInstructions, consigneeInstructions] = await Promise.all([
        normalizedShipperAccount
          ? fetchInstructionsByCustomerCode(normalizedShipperAccount)
          : Promise.resolve(""),
        normalizedConsigneeAccount
          ? fetchInstructionsByCustomerCode(normalizedConsigneeAccount)
          : Promise.resolve(""),
      ]);

      if (!active) {
        return;
      }

      setShipperSpecialInstructions(shipperInstructions);
      setConsigneeSpecialInstructions(consigneeInstructions);
    };

    void loadSpecialInstructions();

    return () => {
      active = false;
    };
  }, [enabled, normalizedConsigneeAccount, normalizedShipperAccount]);

  const openedSpecialInstructionsText =
    openInstructionsFor === "shipper"
      ? shipperSpecialInstructions
      : consigneeSpecialInstructions;

  const openedSpecialInstructionsTitle =
    openInstructionsFor === "shipper"
      ? "Shipper Account # Special Instruction"
      : "Consignee Account # Special Instruction";

  return {
    shipperSpecialInstructions,
    consigneeSpecialInstructions,
    hasShipperSpecialInstructions: Boolean(shipperSpecialInstructions),
    hasConsigneeSpecialInstructions: Boolean(consigneeSpecialInstructions),
    isDialogOpen: openInstructionsFor !== null,
    openedSpecialInstructionsText,
    openedSpecialInstructionsTitle,
    openSpecialInstructions: setOpenInstructionsFor,
    closeSpecialInstructions: () => setOpenInstructionsFor(null),
  };
};
